import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import PageMeta from "../../components/common/PageMeta";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import FileInput from "../../components/form/input/FileInput";
import { EyeIcon, EyeCloseIcon, CalenderIcon } from "../../icons";
import Alert from "../../components/ui/alert/Alert";
import { fetchUserByIdRequest, updateUserRequest } from "../../slices/userSlice";
import { RootState } from "../../store/store";
import {apiRequestWithAuth} from "../../utils/api";
import {Textarea} from "@mantine/core";
import DatePicker from "react-datepicker";
import CustomDateInput from "../../components/form/input/date/CustomDateInput";

export default function EditUser() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user.selectedUser);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rePassword, setRePassword] = useState("");
  const [yearOfBirth, setYearOfBirth] = useState("");
  const [avatar, setAvatar] = useState<File | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showRePassword, setShowRePassword] = useState(false);
  const [error, setError] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [avatarId, setAvatarId] = useState<number>(0);
  const [roles, setRoles] = useState<any[]>([]);
  const [roleId, setRoleId] = useState<number>(1);
  const [status, setStatus] = useState<string>("");
  const [gender, setGender] = useState<string>("");
  const [goal, setGoal] = useState<string>("");
  const [occupation, setOccupation] = useState<string>("");
  const [skill, setSkill] = useState<string>("");
  const [bio, setBio] = useState<string>("");

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    switch (name) {
      case "goal":
        setGoal(value);
        break;
      case "occupation":
        setOccupation(value);
        break;
      case "skill":
        setSkill(value);
        break;
      case "bio":
        setBio(value);
        break;
      default:
        break;
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await apiRequestWithAuth("/roles", "GET", {});
      setRoles(res.result);
    } catch (error) {
      console.error("Lỗi lấy roles:", error);
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchUserByIdRequest(Number(id)));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (user) {
        setName(user.name);
        setEmail(user.email);
        setYearOfBirth(user.yearOfBirth);
        setPreview(user.avatarImage?.publicPath)
        setRoleId(user.roles[0]?.id)
        setStatus(user.status);
        setGender(user.gender)
        setGoal(user.goal || "");
        setOccupation(user.occupation || "");
        setSkill(user.skill || "");
        setBio(user.bio || "");
    }
  }, [user]);

  const parseDate = (dateStr: any): Date | null => {
    if (!dateStr) return null;
    const [day, month, year] = dateStr.split("-");
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // tháng bắt đầu từ 0
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handlePickerChange = async (date: Date | null) => {
    const value = date ? formatDate(date) : "";
    setYearOfBirth(value);
  };

  const uploadFile = async (file: File) => {
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    formDataUpload.append("id", "1");
    formDataUpload.append("subject", "avatar");

    try {
      const res = await apiRequestWithAuth("/media/file", "POST", formDataUpload);
      return res.result?.id || null;
    } catch (error) {
      console.error("Upload failed", error);
      return null;
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatar(file);
      setPreview(URL.createObjectURL(file)); // Hiển thị ảnh preview
      const uploadedId = await uploadFile(file);
      if (uploadedId) {
        setAvatarId(uploadedId);
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== rePassword) {
      setError("Passwords do not match");
      return;
    }
    setError("");

    const formData = new FormData();
    formData.append("name", name);
    formData.append("email", email);
    formData.append("yearOfBirth", yearOfBirth);
    formData.append("roleId", roleId.toString());
    formData.append("status", status.toString());
    formData.append("gender", gender);
    formData.append("goal", goal);
    formData.append("occupation", occupation);
    formData.append("skill", skill);
    formData.append("bio", bio);
    if (password) formData.append("password", password);
    if (avatarId !== null) {
      formData.append("avatar", avatarId.toString());
    }

    if (!id) return <p>User ID is missing</p>;

    dispatch(updateUserRequest({ id: parseInt(id, 10), data: formData }));
    setShowAlert(true);
    setTimeout(() => navigate("/users"), 500);
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  return (
    <div>
      {showAlert && (
        <Alert variant="success" title="Success" message="User updated successfully." />
      )}
      <PageMeta title="Edit User" description="Edit user details" />
      <PageBreadcrumb pageTitle="Edit User" />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-1">
        <div className="space-y-6">
          <ComponentCard title="User Information">
            <div className="space-y-6">
              <div>
                <Label>Name</Label>
                <Input type="text" value={name} onChange={(e) => setName(e.target.value)}/>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {preview && (
                    <div className="mt-2">
                      <img src={preview} alt="Avatar Preview" className="w-32 h-32 object-cover rounded-full border"/>
                    </div>
                )}
                <div className="mt-2">
                  <Label>Avatar</Label>
                  <FileInput onChange={handleFileChange}/>
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}/>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <Label htmlFor="datePicker">Ngày sinh</Label>
                  <div className="relative">
                    <DatePicker
                        selected={parseDate(yearOfBirth)}
                        onChange={handlePickerChange}
                        dateFormat="dd/MM/yyyy"
                        customInput={<CustomDateInput/>}
                        placeholderText="dd/mm/yyyy"
                        isClearable
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        wrapperClassName="w-full"
                        autoComplete="off"
                    />
                  </div>
                </div>
                <div>
                  <Label>Giới tính</Label>
                  <select
                      name="gender"
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                      className="block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  >
                    <option value="">-- Chọn giới tính --</option>
                    <option value="1">Nam</option>
                    z
                    <option value="2">Nữ</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <Label>Password</Label>
                  <div className="relative">
                    <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                    >
                      {showPassword ? <EyeIcon className="fill-gray-500"/> :
                          <EyeCloseIcon className="fill-gray-500"/>}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>Re-Enter Password</Label>
                  <div className="relative">
                    <Input
                        type={showRePassword ? "text" : "password"}
                        placeholder="Confirm your password"
                        value={rePassword}
                        onChange={(e) => setRePassword(e.target.value)}
                    />
                    <button
                        type="button"
                        onClick={() => setShowRePassword(!showRePassword)}
                        className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                    >
                      {showRePassword ? <EyeIcon className="fill-gray-500"/> :
                          <EyeCloseIcon className="fill-gray-500"/>}
                    </button>
                  </div>
                  {error && <p className="text-red-500">{error}</p>}
                </div>
              </div>
              <div>
                <Label>Goal (Mục tiêu)</Label>
                <Textarea name="goal" value={goal} onChange={handleChange}/>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <Label>Occupation (Nghề nghiệp)</Label>
                  <Input type="text" value={occupation}
                         onChange={(e) => setOccupation(e.target.value)}/>
                </div>
                <div>
                  <Label>Skill (Chuyên môn/Kỹ năng)</Label>
                  <Input type="text" value={skill}
                         onChange={(e) => setSkill(e.target.value)}/>
                </div>
              </div>
              <div>
                <Label>Bio (Quá trình học tập)</Label>
                <Textarea name="bio" value={bio} onChange={handleChange}/>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <Label>Quyền</Label>
                  <select
                      name="roleId"
                      value={roleId}
                      onChange={(e) => {
                        const value = e.target.value;
                        setRoleId(value ? parseInt(value) : 0);
                      }}
                      className="block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  >
                    <option value="">-- Chọn quyền --</option>
                    {roles.map(role => (
                        <option key={role.id} value={role.id}>
                          {role.displayName}
                        </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Trạng thái</Label>
                  <select
                      name="status"
                      value={status}
                      onChange={(e) => {
                        const value = e.target.value;
                        setStatus(value);
                      }}
                      className="block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  >
                    <option value="1">Active</option>
                    <option value="2">Inactive</option>
                    <option value="2">Deleted</option>
                  </select>
                </div>
              </div>
            </div>
          </ComponentCard>
        </div>
        <button onClick={handleSubmit}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300">
          Save Changes
        </button>
      </div>
    </div>
  );
}
